import React, { useState, useEffect, useCallback } from 'react';
import { Button, Skeleton } from '../../components/ui';
import { adminAPI } from '../../services/adminApi';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import {
  Search,
  Close,
  Support,
  Send,
  Person,
  AccessTime,
  PriorityHigh,
  CheckCircle,
  Warning,
  Email,
  Phone,
  ExpandMore,
  ExpandLess,
  Circle,
  ArrowBack
} from '../../mui-icons';
import './SupportPage.css';

const SupportPage = () => {
  const { canEdit, admin } = useAdminAuth();
  
  // Dashboard State
  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  
  // List State
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  
  // Detail View State
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Reply State
  const [replyMessage, setReplyMessage] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [replyError, setReplyError] = useState('');
  
  // Update State
  const [updateLoading, setUpdateLoading] = useState(false);

  // Load dashboard
  const loadDashboard = async () => {
    try {
      const response = await adminAPI.getSupportDashboard();
      if (response.status === 'success') {
        setDashboard(response.data);
      }
    } catch (err) {
      console.error('Erro ao carregar dashboard:', err);
    } finally {
      setDashboardLoading(false);
    }
  };

  // Load tickets
  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(statusFilter && { status: statusFilter }),
        ...(priorityFilter && { prioridade: priorityFilter }),
        ...(categoryFilter && { categoria: categoryFilter }),
        ...(search && { search })
      };

      const response = await adminAPI.getSupportTickets(params);
      
      if (response.status === 'success') {
        setTickets(response.data.items || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          total_pages: response.data.pagination?.total_pages || 0
        }));
      }
    } catch (err) {
      console.error('Erro ao carregar tickets:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, statusFilter, priorityFilter, categoryFilter, search]);

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // Load ticket detail
  const loadTicketDetail = async (ticketId) => {
    setDetailLoading(true);
    try {
      const response = await adminAPI.getSupportTicket(ticketId);
      if (response.status === 'success') {
        setTicketDetail(response.data);
      }
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Open ticket detail
  const openTicketDetail = (ticket) => {
    setSelectedTicket(ticket);
    setReplyMessage('');
    setReplyError('');
    loadTicketDetail(ticket.idsupportticket);
  };

  // Close ticket detail
  const closeTicketDetail = () => {
    setSelectedTicket(null);
    setTicketDetail(null);
  };

  // Handle reply
  const handleReply = async () => {
    if (!replyMessage.trim()) {
      setReplyError('Digite uma mensagem');
      return;
    }

    setReplyLoading(true);
    setReplyError('');

    try {
      await adminAPI.replySupportTicket(selectedTicket.idsupportticket, replyMessage);
      setReplyMessage('');
      loadTicketDetail(selectedTicket.idsupportticket);
      loadTickets();
    } catch (err) {
      setReplyError(err.message);
    } finally {
      setReplyLoading(false);
    }
  };

  // Handle update ticket
  const handleUpdateTicket = async (field, value) => {
    setUpdateLoading(true);
    try {
      await adminAPI.updateSupportTicket(selectedTicket.idsupportticket, { [field]: value });
      loadTicketDetail(selectedTicket.idsupportticket);
      loadTickets();
      loadDashboard();
    } catch (err) {
      alert(err.message);
    } finally {
      setUpdateLoading(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      aberto: { label: 'Aberto', class: 'status-info' },
      aguardando_resposta: { label: 'Aguardando Resposta', class: 'status-warning' },
      respondido: { label: 'Respondido', class: 'status-success' },
      resolvido: { label: 'Resolvido', class: 'status-success' },
      fechado: { label: 'Fechado', class: 'status-default' }
    };
    const { label, class: className } = statusMap[status] || { label: status, class: '' };
    return <span className={`status-badge ${className}`}>{label}</span>;
  };

  // Get priority badge
  const getPriorityBadge = (priority) => {
    const priorityMap = {
      baixa: { label: 'Baixa', class: 'priority-low' },
      media: { label: 'Média', class: 'priority-medium' },
      alta: { label: 'Alta', class: 'priority-high' },
      urgente: { label: 'Urgente', class: 'priority-urgent' }
    };
    const { label, class: className } = priorityMap[priority] || { label: priority, class: '' };
    return <span className={`priority-badge ${className}`}>{label}</span>;
  };

  return (
    <div className="support-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <Support sx={{ fontSize: 32 }} />
          <div>
            <h1>Suporte</h1>
            <p>Gestão de tickets de suporte</p>
          </div>
        </div>
      </div>

      {/* Dashboard Cards */}
      {!dashboardLoading && dashboard && (
        <div className="support-dashboard">
          <div className="dashboard-card pn-card pn-card--default">
            <div className="dashboard-card pn-card pn-card--default-icon warning">
              <AccessTime />
            </div>
            <div className="dashboard-card pn-card pn-card--default-content">
              <span className="dashboard-card pn-card pn-card--default-label">Aguardando Resposta</span>
              <span className="dashboard-card pn-card pn-card--default-value">{dashboard.by_status?.aguardando_resposta || 0}</span>
            </div>
          </div>
          
          <div className="dashboard-card pn-card pn-card--default">
            <div className="dashboard-card pn-card pn-card--default-icon urgent">
              <PriorityHigh />
            </div>
            <div className="dashboard-card pn-card pn-card--default-content">
              <span className="dashboard-card pn-card pn-card--default-label">Urgentes</span>
              <span className="dashboard-card pn-card pn-card--default-value">{dashboard.by_priority?.urgente || 0}</span>
            </div>
          </div>
          
          <div className="dashboard-card pn-card pn-card--default">
            <div className="dashboard-card pn-card pn-card--default-icon danger">
              <Warning />
            </div>
            <div className="dashboard-card pn-card pn-card--default-content">
              <span className="dashboard-card pn-card pn-card--default-label">Atrasados (+24h)</span>
              <span className="dashboard-card pn-card pn-card--default-value">{dashboard.sla?.overdue_24h || 0}</span>
            </div>
          </div>
          
          <div className="dashboard-card pn-card pn-card--default">
            <div className="dashboard-card pn-card pn-card--default-icon success">
              <CheckCircle />
            </div>
            <div className="dashboard-card pn-card pn-card--default-content">
              <span className="dashboard-card pn-card pn-card--default-label">Resolvidos (7 dias)</span>
              <span className="dashboard-card pn-card pn-card--default-value">{dashboard.overview?.resolved_7_days || 0}</span>
            </div>
          </div>

          <div className="dashboard-card pn-card pn-card--default wide">
            <div className="dashboard-card pn-card pn-card--default-content">
              <span className="dashboard-card pn-card pn-card--default-label">Tempo Médio de Resposta</span>
              <span className="dashboard-card pn-card pn-card--default-value">
                {dashboard.sla?.avg_first_response_minutes 
                  ? `${Math.round(dashboard.sla.avg_first_response_minutes)} min`
                  : '-'
                }
              </span>
              <span className="dashboard-card pn-card pn-card--default-subtext">
                Avaliação média: {dashboard.ratings?.average?.toFixed(1) || '-'} ⭐
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="support-content">
        {/* Tickets List */}
        <div className="tickets-panel">
          {/* Filters */}
          <div className="tickets-filters">
            <div className="search-box">
              <Search className="search-icon" sx={{ fontSize: 20 }} />
              <input 
                type="text"
                placeholder="Buscar por número, assunto ou email..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              />
            </div>
            
            <select 
              className="filter-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="">Todos os Status</option>
              <option value="aberto">Aberto</option>
              <option value="aguardando_resposta">Aguardando Resposta</option>
              <option value="respondido">Respondido</option>
              <option value="resolvido">Resolvido</option>
              <option value="fechado">Fechado</option>
            </select>
            
            <select 
              className="filter-select"
              value={priorityFilter}
              onChange={(e) => {
                setPriorityFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="">Todas Prioridades</option>
              <option value="urgente">Urgente</option>
              <option value="alta">Alta</option>
              <option value="media">Média</option>
              <option value="baixa">Baixa</option>
            </select>
          </div>

          {/* Tickets List */}
          <div className="tickets-list">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <div key={i} className="ticket-item skeleton-item">
                  <Skeleton width="100%" height={80} borderRadius={8} />
                </div>
              ))
            ) : error ? (
              <div className="error-state">
                <Warning sx={{ fontSize: 48 }} />
                <p>{error}</p>
                <Button variant="primary" onClick={loadTickets}>
                  Tentar novamente
                </Button>
              </div>
            ) : tickets.length === 0 ? (
              <div className="empty-state">
                <Support sx={{ fontSize: 48, opacity: 0.3 }} />
                <p>Nenhum ticket encontrado</p>
              </div>
            ) : (
              tickets.map(ticket => (
                <div 
                  key={ticket.idsupportticket}
                  className={`ticket-item ${selectedTicket?.idsupportticket === ticket.idsupportticket ? 'active' : ''} ${ticket.mensagens_nao_lidas > 0 ? 'unread' : ''}`}
                  onClick={() => openTicketDetail(ticket)}
                >
                  <div className="ticket-header">
                    <span className="ticket-number">{ticket.ticket_number}</span>
                    {getPriorityBadge(ticket.prioridade)}
                    {ticket.mensagens_nao_lidas > 0 && (
                      <span className="unread-badge">{ticket.mensagens_nao_lidas}</span>
                    )}
                  </div>
                  <div className="ticket-subject">{ticket.assunto}</div>
                  <div className="ticket-meta">
                    <span className="ticket-user">{ticket.user?.name}</span>
                    <span className="ticket-category">{ticket.categoria}</span>
                    {getStatusBadge(ticket.status)}
                  </div>
                  <div className="ticket-date">{formatDate(ticket.updated_at)}</div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="tickets-pagination">
              <button 
                className="pagination-btn"
                disabled={pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                Anterior
              </button>
              <span>{pagination.page} / {pagination.total_pages}</span>
              <button 
                className="pagination-btn"
                disabled={pagination.page === pagination.total_pages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                Próxima
              </button>
            </div>
          )}
        </div>

        {/* Ticket Detail or Placeholder */}
        {!selectedTicket ? (
          <div className="no-ticket-placeholder">
            <Support sx={{ fontSize: 64 }} />
            <h3>Selecione um ticket</h3>
            <p>Escolha um ticket na lista ao lado para visualizar os detalhes e responder.</p>
          </div>
        ) : (
          <div className="ticket-detail-panel">
            <div className="ticket-detail-header">
              <button className="back-btn" onClick={closeTicketDetail}>
                <ArrowBack />
              </button>
              <div className="ticket-detail-title">
                <span className="ticket-number">{selectedTicket.ticket_number}</span>
                <h2>{selectedTicket.assunto}</h2>
              </div>
            </div>

            {detailLoading ? (
              <div className="ticket-detail-loading">
                <Skeleton width="100%" height={200} borderRadius={8} />
              </div>
            ) : ticketDetail ? (
              <>
                {/* User Info */}
                <div className="ticket-user-info">
                  <div className="user-avatar">
                    <Person sx={{ fontSize: 32 }} />
                  </div>
                  <div className="user-details">
                    <strong>{ticketDetail.ticket?.user?.name}</strong>
                    <div className="user-contact">
                      <span><Email sx={{ fontSize: 14 }} /> {ticketDetail.ticket?.user?.mail}</span>
                      {ticketDetail.ticket?.user?.tel && (
                        <span><Phone sx={{ fontSize: 14 }} /> {ticketDetail.ticket?.user?.tel}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Ticket Info */}
                <div className="ticket-info-grid">
                  <div className="ticket-info-item">
                    <label>Status</label>
                    {canEdit() ? (
                      <select 
                        className="admin-input"
                        value={ticketDetail.ticket?.status || ''}
                        onChange={(e) => handleUpdateTicket('status', e.target.value)}
                        disabled={updateLoading}
                      >
                        <option value="aberto">Aberto</option>
                        <option value="aguardando_resposta">Aguardando Resposta</option>
                        <option value="respondido">Respondido</option>
                        <option value="resolvido">Resolvido</option>
                        <option value="fechado">Fechado</option>
                      </select>
                    ) : (
                      getStatusBadge(ticketDetail.ticket?.status)
                    )}
                  </div>
                  <div className="ticket-info-item">
                    <label>Prioridade</label>
                    {canEdit() ? (
                      <select 
                        className="admin-input"
                        value={ticketDetail.ticket?.prioridade || ''}
                        onChange={(e) => handleUpdateTicket('prioridade', e.target.value)}
                        disabled={updateLoading}
                      >
                        <option value="baixa">Baixa</option>
                        <option value="media">Média</option>
                        <option value="alta">Alta</option>
                        <option value="urgente">Urgente</option>
                      </select>
                    ) : (
                      getPriorityBadge(ticketDetail.ticket?.prioridade)
                    )}
                  </div>
                  <div className="ticket-info-item">
                    <label>Categoria</label>
                    <span>{ticketDetail.ticket?.categoria}</span>
                  </div>
                  <div className="ticket-info-item">
                    <label>Criado em</label>
                    <span>{formatDate(ticketDetail.ticket?.created_at)}</span>
                  </div>
                </div>

                {/* Messages */}
                <div className="ticket-messages">
                  <h3>Mensagens ({ticketDetail.total_messages || 0})</h3>
                  <div className="messages-list">
                    {ticketDetail.messages?.map(message => (
                      <div 
                        key={message.idsupportmessage}
                        className={`message-item ${message.is_admin ? 'admin' : 'user'}`}
                      >
                        <div className="message-header">
                          <span className="message-author">
                            {message.is_admin ? '🛡️ ' : '👤 '}
                            {message.autor_name}
                          </span>
                          <span className="message-date">{formatDate(message.created_at)}</span>
                        </div>
                        <div className="message-content">
                          {message.mensagem}
                        </div>
                        {!message.lido_em && !message.is_admin && (
                          <span className="message-unread">Não lido</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Reply Box */}
                {canEdit() && ticketDetail.ticket?.status !== 'fechado' && (
                  <div className="reply-box">
                    {replyError && (
                      <div className="form-error">
                        <Warning sx={{ fontSize: 18 }} />
                        {replyError}
                      </div>
                    )}
                    <textarea 
                      className="reply-textarea"
                      placeholder="Digite sua resposta..."
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      disabled={replyLoading}
                    />
                    <div className="reply-actions">
                      <Button
                        variant="primary"
                        onClick={handleReply}
                        disabled={replyLoading || !replyMessage.trim()}
                        loading={replyLoading}
                      >
                        <Send sx={{ fontSize: 18 }} />
                        {replyLoading ? 'Enviando...' : 'Enviar Resposta'}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportPage;

